import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, Dumbbell, Users } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import InviteDialog from "@/components/InviteDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { name: "", email: "", phone: "", specialties: "", bio: "", status: "active" };

export default function Trainers() {
  const [trainers, setTrainers] = useState(null);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [invite, setInvite] = useState(null);

  const load = () => api.get("/trainers").then((r) => setTrainers(r.data));
  useEffect(() => { load(); api.get("/branches").then((r) => setBranches(r.data)); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ name: t.name, email: t.email || "", phone: t.phone || "", specialties: (t.specialties || []).join(", "), bio: t.bio || "", status: t.status }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const specialties = form.specialties.split(",").map((s) => s.trim()).filter(Boolean);
      if (editing) {
        await api.put(`/trainers/${editing.id}`, { name: form.name, phone: form.phone, specialties, bio: form.bio, status: form.status });
        toast.success("Antrenör güncellendi");
      } else {
        const res = await api.post("/trainers", { name: form.name, email: form.email, phone: form.phone, specialties, bio: form.bio, status: form.status, branch_id: branches[0]?.id });
        toast.success("Antrenör oluşturuldu");
        if (res.data?.invite_token) setInvite({ name: res.data.name || form.name, token: res.data.invite_token });
      }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-trainers-page">
      <PageHeader title="Antrenörler" subtitle={`${trainers?.length ?? "—"} antrenör`}
        action={<Button onClick={openNew} data-testid="add-new-trainer-button" className="bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold rounded-xl"><Plus size={18} className="mr-1" /> Yeni Antrenör</Button>} />

      {trainers === null ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-40 rounded-2xl bg-[#13131C]" />)}</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {trainers.map((t) => (
            <div key={t.id} data-testid={`trainer-card-${t.id}`} className="rounded-2xl bg-[#13131C] border border-[#222230] p-5 hover:border-[#FF5500]/40 transition-all group">
              <div className="flex items-start justify-between">
                <Avatar className="w-14 h-14"><AvatarFallback className="bg-[#FF5500]/15 text-[#FF5500] text-lg font-bold">{t.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}</AvatarFallback></Avatar>
                <button onClick={() => openEdit(t)} data-testid={`edit-trainer-${t.id}`} className="p-2 rounded-lg text-[#68687B] hover:text-[#FF5500] hover:bg-[#252536] opacity-0 group-hover:opacity-100 transition-opacity"><Pencil size={16} /></button>
              </div>
              <h3 className="font-display font-bold text-white text-lg mt-3">{t.name}</h3>
              <p className="text-xs text-[#68687B]">{t.email}</p>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(t.specialties || []).slice(0, 3).map((s) => <span key={s} className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-[#1B1B27] text-[#A0A0B2] border border-[#2D2D3F]">{s}</span>)}
              </div>
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#1A1A24]">
                <span className="flex items-center gap-1.5 text-sm text-[#A0A0B2]"><Users size={15} /> {t.member_count} üye</span>
                <StatusBadge status={t.status} />
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#13131C] border-[#222230] text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Antrenör Düzenle" : "Yeni Antrenör"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><label className="text-xs text-[#A0A0B2] mb-1 block">Ad Soyad</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="trainer-form-name" className="bg-[#0E0E14] border-[#262636] text-white" /></div>
            {!editing && <div><label className="text-xs text-[#A0A0B2] mb-1 block">E-posta</label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="trainer-form-email" className="bg-[#0E0E14] border-[#262636] text-white" /></div>}
            <div><label className="text-xs text-[#A0A0B2] mb-1 block">Telefon</label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#0E0E14] border-[#262636] text-white" /></div>
            <div><label className="text-xs text-[#A0A0B2] mb-1 block">Uzmanlık (virgülle)</label><Input value={form.specialties} onChange={(e) => setForm({ ...form, specialties: e.target.value })} placeholder="Pilates, EMS" className="bg-[#0E0E14] border-[#262636] text-white" /></div>
            <div><label className="text-xs text-[#A0A0B2] mb-1 block">Biyografi</label><Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="bg-[#0E0E14] border-[#262636] text-white" /></div>
            <div><label className="text-xs text-[#A0A0B2] mb-1 block">Durum</label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="bg-[#0E0E14] border-[#262636] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#181824] border-[#2D2D3F] text-white"><SelectItem value="active">Aktif</SelectItem><SelectItem value="passive">Pasif</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-[#A0A0B2]">İptal</Button>
            <Button onClick={save} disabled={saving || !form.name} data-testid="trainer-form-submit" className="bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold">{saving ? <Loader2 className="animate-spin" size={16} /> : "Kaydet"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <InviteDialog invite={invite} onClose={() => setInvite(null)} />
    </div>
  );
}
